import React, { useState, useMemo, useEffect, forwardRef } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import { useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Button,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  Text,
  Select,
  Flex,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
} from '@chakra-ui/react';
import { CalendarIcon } from '@chakra-ui/icons';

import { nextBillingDate } from '../api';
import { useModalManagerContext } from '../../components/ModalManager/ModalManagerContext';
import { HUMAN_DATE_FORMAT } from '../../utils/constants';
import { useGlobalContext } from '../state/GlobalContext';

enum Options {
  ONE_MONTH = 'ONE_MONTH',
  TWO_MONTH = 'TWO_MONTH',
  THREE_MONTH = 'THREE_MONTH',
  CUSTOM = 'CUSTOM',
}

const DelaySubscriptionStep: React.FC = () => {
  const { t } = useTranslation();
  const { loadPreviousFlowStep, data, onClose } = useModalManagerContext();
  const nextBilling = useMemo(
    () => dayjs(data?.subscription?.NextBillingDate),
    [data],
  );

  const [selectedOption, setSelectedOption] = useState<Options>(
    Options.ONE_MONTH,
  );

  const [datePickerValue, setDatePickerValue] = useState<Dayjs>(
    dayjs(nextBilling),
  );
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs>(() =>
    nextBilling.add(1, 'month'),
  );

  const { customerPortalSettings, customerId, subscriptionId, authToken } =
    useGlobalContext();

  const isPauseSuppressionEnabled = !customerPortalSettings?.pauseSuppression;
  useEffect(() => {
    switch (selectedOption) {
      case Options.ONE_MONTH:
        setSelectedDate(nextBilling.add(1, 'month'));
        break;
      case Options.TWO_MONTH:
        setSelectedDate(nextBilling.add(2, 'month'));
        break;
      case Options.THREE_MONTH:
        setSelectedDate(nextBilling.add(3, 'month'));
        break;
      default:
        break;
    }
  }, [selectedOption, nextBilling]);

  const queryClient = useQueryClient();

  const { isLoading, mutate } = useMutation(
    () =>
      nextBillingDate(
        selectedDate,
        customerId,
        subscriptionId,
        authToken,
        'UPDATED_ORDER_DATE',
      ),

    {
      onSuccess: async (data) => {
        const previousValue = queryClient.getQueryData<Subscription>([
          'subscription',
          customerId,
          subscriptionId,
        ]);
        const newDate = selectedDate.toDate().toLocaleDateString();

        onClose(
          t('cancelModal.skip.success', {
            date: newDate,
          }),
        );
        queryClient.setQueryData(['subscription', customerId, subscriptionId], {
          ...previousValue,
          NextBillingDate: data.NextBillingDate,
        });
      },

      onError: (err, variables, previousValue) => {
        console.error(err);
        onClose(t('cancelModal.error'), { type: 'error' });
        queryClient.setQueryData(
          ['subscription', customerId, subscriptionId],
          previousValue,
        );
      },
    },
  );

  const handleOptionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedOption(Options[e.target.value]);
  };

  const handleDatePickerChange = (value: string | Date) => {
    setDatePickerValue(dayjs(value));
    setSelectedDate(dayjs(value));
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutate();
  };

  const DatePickerInput = forwardRef(
    ({ value, onClick, onChange }: any, ref) => (
      <InputGroup onClick={onClick}>
        <Input
          aria-label={t('tables.next_order')}
          placeholder={value}
          value={value}
          onChange={onChange}
        />
        <InputRightElement>
          <CalendarIcon />
        </InputRightElement>
      </InputGroup>
    ),
  );

  return (
    <>
      <ModalHeader>
        {isPauseSuppressionEnabled
          ? t('pauseModal.delaySubscription')
          : t('pauseModal.pauseModalTitle')}
      </ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <Box>
          <Text>
            {t('subscription.pause_disclaimer_date', {
              date: selectedDate.format(HUMAN_DATE_FORMAT),
            })}
          </Text>
        </Box>
        <Box paddingTop="6">
          <form onSubmit={handleFormSubmit} id="delay_form">
            <Flex mb="6">
              <FormControl id="delayTime">
                <Select
                  name="delayTime"
                  required
                  onChange={handleOptionChange}
                  value={selectedOption}
                >
                  {Object.keys(Options)
                    .filter(
                      (key) =>
                        isPauseSuppressionEnabled ||
                        (!isPauseSuppressionEnabled && key !== Options.CUSTOM),
                    )
                    .map((key) => (
                      <option key={key} value={key}>
                        {t(`pauseModal.${Options[key]}`)}
                      </option>
                    ))}
                </Select>
                <FormLabel>
                  {isPauseSuppressionEnabled
                    ? t('pauseModal.delaySubscription')
                    : t('pauseModal.pauseModalLabel')}
                </FormLabel>
              </FormControl>

              {selectedOption === Options.CUSTOM && (
                <div style={{ marginLeft: 20 }}>
                  <DatePicker
                    placeholderText={datePickerValue.format('DD/MM/YYYY')}
                    selected={datePickerValue.toDate()}
                    minDate={new Date(nextBilling.format('YYYY-MM-DD'))}
                    onChange={handleDatePickerChange}
                    customInput={<DatePickerInput />}
                  />
                </div>
              )}
            </Flex>
          </form>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Button
          mr="4"
          onClick={loadPreviousFlowStep}
          variant="outlinedBig"
          isLoading={isLoading}
        >
          {t('pauseModal.back')}
        </Button>
        <Button
          type="submit"
          variant="blackBig"
          form="delay_form"
          isLoading={isLoading}
        >
          {t('pauseModal.save')}
        </Button>
      </ModalFooter>
    </>
  );
};

export default DelaySubscriptionStep;
